/**
 * KawaiiBobaCup — chubby, cheek-blush, arms-up dancing boba cup inspired
 * by the user's reference art. Bigger smile, pinker cheeks, tiny waving
 * arms, smooth rounded edges. Wrap in `bp-dance` to make it groove.
 *
 * Use the `variant` prop for one of the preset color palettes:
 *   - "lilac"   (purple, default)
 *   - "matcha"  (green with a leaf hat)
 *   - "mango"   (yellow/orange with a leaf-stem)
 *   - "pink"    (strawberry-milk)
 *   - "taro"    (lavender)
 */
import { memo } from "react";

const VARIANTS = {
  lilac:  { drink: "#c4b5fd", cap: "#7c3aed", straw: "#fbbf24", pearl: "#3b1f10", shadow: "#a78bfa", topper: null },
  matcha: { drink: "#a7e8b3", cap: "#16a34a", straw: "#fde047", pearl: "#15803d", shadow: "#4ade80", topper: "leaf" },
  mango:  { drink: "#fcd34d", cap: "#f97316", straw: "#22c55e", pearl: "#92400e", shadow: "#fbbf24", topper: "stem" },
  pink:   { drink: "#fbcfe8", cap: "#ec4899", straw: "#a855f7", pearl: "#9d174d", shadow: "#f9a8d4", topper: null },
  taro:   { drink: "#d8b4fe", cap: "#8b5cf6", straw: "#06b6d4", pearl: "#581c87", shadow: "#c4b5fd", topper: null },
};

function KawaiiBobaCup({
  variant = "lilac",
  className = "w-32 h-40",
  raisedArms = true,
  flip = false,
  ...rest
}) {
  const v = VARIANTS[variant] || VARIANTS.lilac;
  return (
    <svg
      viewBox="0 0 120 150"
      className={className}
      role="img"
      aria-label={`${variant} boba cup`}
      style={flip ? { transform: "scaleX(-1)" } : undefined}
      {...rest}
    >
      <defs>
        <linearGradient id={`kbc-${variant}-drink`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={v.drink} stopOpacity="1" />
          <stop offset="100%" stopColor={v.shadow} stopOpacity="1" />
        </linearGradient>
        <radialGradient id={`kbc-${variant}-shine`} cx="0.3" cy="0.2" r="0.6">
          <stop offset="0%" stopColor="#ffffff" stopOpacity="0.92" />
          <stop offset="35%" stopColor="#ffffff" stopOpacity="0.4" />
          <stop offset="70%" stopColor="#ffffff" stopOpacity="0" />
        </radialGradient>
      </defs>

      {/* Tiny leaf or stem topper for matcha / mango */}
      {v.topper === "leaf" && (
        <g>
          <ellipse cx="60" cy="14" rx="12" ry="6" fill="#22c55e" transform="rotate(-20 60 14)" />
          <path d="M55 18 Q60 8 70 14" stroke="#15803d" strokeWidth="1.5" fill="none" />
        </g>
      )}
      {v.topper === "stem" && (
        <g>
          <rect x="58" y="6" width="4" height="10" rx="2" fill="#16a34a" />
          <ellipse cx="68" cy="10" rx="8" ry="4" fill="#22c55e" transform="rotate(20 68 10)" />
        </g>
      )}

      {/* Straw */}
      <g>
        <path
          d="M76 20 Q72 50 64 75"
          stroke={v.straw}
          strokeWidth="8"
          strokeLinecap="round"
          fill="none"
        />
        <path
          d="M76 20 Q72 50 64 75"
          stroke="#ffffff"
          strokeOpacity="0.45"
          strokeWidth="2.5"
          strokeLinecap="round"
          fill="none"
        />
      </g>

      {/* Lid (rounded dome — extra plump) */}
      <path
        d="M22 38 Q60 14 98 38 Q98 46 90 46 L30 46 Q22 46 22 38 Z"
        fill={v.cap}
        stroke="#1f1340"
        strokeWidth="2.5"
        strokeLinejoin="round"
      />
      <path
        d="M30 32 Q60 22 90 32"
        stroke="#ffffff"
        strokeOpacity="0.5"
        strokeWidth="3"
        fill="none"
        strokeLinecap="round"
      />

      {/* Cup body — rounded chubby */}
      <path
        d="M26 46 L94 46 L88 130 Q86 142 76 142 L44 142 Q34 142 32 130 Z"
        fill={`url(#kbc-${variant}-drink)`}
        stroke="#1f1340"
        strokeWidth="2.5"
        strokeLinejoin="round"
      />

      {/* Body shine */}
      <ellipse cx="44" cy="78" rx="13" ry="26" fill={`url(#kbc-${variant}-shine)`} />
      {/* Specular highlight dot for a glossy, dimensional pop */}
      <circle cx="39" cy="60" r="4.5" fill="#ffffff" opacity="0.6" />

      {/* Pearls */}
      <g fill={v.pearl}>
        <circle cx="42" cy="120" r="5.5" />
        <circle cx="55" cy="125" r="5.8" />
        <circle cx="68" cy="120" r="5.2" />
        <circle cx="78" cy="124" r="5.5" />
        <circle cx="48" cy="130" r="4.8" />
        <circle cx="64" cy="132" r="5.1" />
        <circle cx="74" cy="134" r="4.5" />
      </g>
      {/* Pearl highlights */}
      <g fill="#ffffff" opacity="0.55">
        <circle cx="40" cy="118" r="1.4" />
        <circle cx="53" cy="123" r="1.4" />
        <circle cx="66" cy="118" r="1.3" />
        <circle cx="76" cy="122" r="1.4" />
      </g>

      {/* Big kawaii face */}
      <g>
        {/* Eyes — big ovals with shine */}
        <ellipse cx="46" cy="80" rx="3.5" ry="5" fill="#1f1340" />
        <ellipse cx="74" cy="80" rx="3.5" ry="5" fill="#1f1340" />
        <circle cx="47.5" cy="78" r="1.7" fill="#ffffff" />
        <circle cx="75.5" cy="78" r="1.7" fill="#ffffff" />
        <circle cx="44.6" cy="81.5" r="0.8" fill="#ffffff" opacity="0.8" />
        <circle cx="72.6" cy="81.5" r="0.8" fill="#ffffff" opacity="0.8" />

        {/* Smile */}
        <path
          d="M46 92 Q60 102 74 92"
          stroke="#1f1340"
          strokeWidth="2.5"
          fill="none"
          strokeLinecap="round"
        />
        {/* Tiny tongue */}
        <path
          d="M55 95 Q60 100 65 95"
          fill="#ec4899"
          opacity="0.85"
        />

        {/* Pink blush cheeks */}
        <ellipse cx="36" cy="90" rx="5" ry="3" fill="#fda4af" opacity="0.85" />
        <ellipse cx="84" cy="90" rx="5" ry="3" fill="#fda4af" opacity="0.85" />
      </g>

      {/* Tiny waving arms */}
      {raisedArms && (
        <g stroke="#1f1340" strokeWidth="3" strokeLinecap="round" fill={v.drink}>
          {/* Left arm raised up */}
          <path d="M28 70 Q20 56 16 44" fill="none" />
          <circle cx="16" cy="44" r="5" stroke="#1f1340" />
          {/* Right arm raised up */}
          <path d="M92 70 Q100 56 104 44" fill="none" />
          <circle cx="104" cy="44" r="5" stroke="#1f1340" />
        </g>
      )}

      {/* Tiny feet */}
      <g fill="#1f1340">
        <ellipse cx="46" cy="144" rx="6" ry="3" />
        <ellipse cx="74" cy="144" rx="6" ry="3" />
      </g>
    </svg>
  );
}

export default memo(KawaiiBobaCup);
